import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { AlertTriangle, Heart, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../lib/api';
import { ReportIssueDialog } from './ReportIssueDialog';

const BACKEND_URL = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL || "");

/**
 * Shared header for logged-in Akademie-Mitglieder – identical look & feel on
 * every profile page (portal, seminars, contact details, …).
 *
 * Renders a 3-item mini-navigation:  Mein Bereich · Seminare · Meine Kontaktdaten
 * Plus a prominent "Abmelden"-Button. Breadcrumbs are rendered separately
 * via <MemberBreadcrumb /> directly below this header.
 */
const NAV_ITEMS = [
    { to: '/portal', label: 'Mein Bereich', matches: ['/portal'], badgeKey: 'mein_bereich' },
    { to: '/seminare', label: 'Seminare', matches: ['/seminare'], badgeKey: 'seminare' },
    { to: '/lizenzverlaengerung', label: 'Meine Lizenz verlängern', matches: ['/lizenzverlaengerung'], badgeKey: 'lizenz' },
    { to: '/kontaktdaten', label: 'Meine Kontaktdaten', matches: ['/kontaktdaten'], badgeKey: 'kontaktdaten' },
    { to: '/portal/hilfe', label: 'Hilfe & FAQ', matches: ['/portal/hilfe'], badgeKey: 'hilfe' },
];

export function MemberHeader() {
    const { user, logout, isAuthenticated } = useAuth();
    const location = useLocation();
    const [badges, setBadges] = useState({ mein_bereich: 0, seminare: 0, kontaktdaten: 0, lizenz: 0 });
    const [headerLogo, setHeaderLogo] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    useEffect(() => {
        const url = `${BACKEND_URL}/api/settings/logos/header`;
        const img = new Image();
        img.onload = () => setHeaderLogo(url);
        img.onerror = () => setHeaderLogo(null);
        img.src = url;
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;
        const load = () => authApi.getBadges()
            .then((r) => setBadges(r.data))
            .catch(() => {});
        load();
        // Refresh on route change so action-driven updates are visible quickly
    }, [isAuthenticated, location.pathname]);

    // Close mobile menu whenever the route changes so it doesn't linger open
    // after navigation.
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    const handleLogout = () => {
        // Hart navigieren via window.location — vermeidet, dass ProtectedRoute
        // während des Auth-State-Wechsels zwischenzeitlich zu /login umleitet
        // (React-Router-Batching allein reicht dafür nicht aus, siehe iter83).
        logout();
        window.location.replace('/seminare');
    };

    const isActive = (item) => item.matches.some((m) => location.pathname.startsWith(m));

    // Total unread across all nav items — surfaced on the mobile hamburger
    // button so users get a heads-up even when the menu is collapsed.
    const totalBadges = Object.values(badges).reduce((s, v) => s + (Number(v) || 0), 0);

    return (
        <header className="bg-secondary" data-testid="member-header">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
                <div className="flex items-center justify-between gap-4">
                    <Link to="/portal" className="flex items-center gap-3 min-w-0" data-testid="member-header-logo">
                        {headerLogo ? (
                            <img src={headerLogo} alt="BGPR-Akademie" className="h-10 w-auto object-contain shrink-0" />
                        ) : (
                            <div className="p-2 bg-primary rounded-xl shrink-0">
                                <Heart className="h-5 w-5 text-white" />
                            </div>
                        )}
                        <div className="min-w-0">
                            <h1 className="text-lg font-bold text-white leading-tight truncate">BGPR-Akademie</h1>
                            {user && (
                                <p className="text-xs text-gray-400 truncate">{user.first_name} {user.last_name}</p>
                            )}
                        </div>
                    </Link>

                    {/* Desktop nav (md+) — full inline nav */}
                    <nav className="hidden md:flex items-center gap-1 lg:gap-2" aria-label="Hauptnavigation">
                        {NAV_ITEMS.map((item) => {
                            const active = isActive(item);
                            const count = Number(badges[item.badgeKey] || 0);
                            return (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    className={`relative px-3 py-1.5 rounded-md text-sm transition-colors ${
                                        active
                                            ? 'bg-white/15 text-white font-semibold'
                                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                                    }`}
                                    aria-current={active ? 'page' : undefined}
                                    data-testid={`member-nav-${item.to.replace('/', '')}`}
                                >
                                    {item.label}
                                    {count > 0 && (
                                        <span
                                            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none shadow-sm ring-2 ring-secondary"
                                            aria-label={`${count} neue Meldung${count === 1 ? '' : 'en'}`}
                                            data-testid={`member-nav-badge-${item.to.replace('/', '')}`}
                                        >
                                            {count > 9 ? '9+' : count}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                        <Button
                            variant="ghost" size="sm" onClick={() => setReportOpen(true)}
                            className="ml-1 text-gray-300 hover:text-white hover:bg-white/10 min-h-[44px]"
                            data-testid="member-header-report-issue"
                            title="Fehler melden"
                        >
                            <AlertTriangle className="h-4 w-4 mr-1" /> Fehler melden
                        </Button>
                        <Button
                            variant="ghost" size="sm" onClick={handleLogout}
                            className="ml-1 text-gray-300 hover:text-white hover:bg-white/10 min-h-[44px]"
                            data-testid="member-header-logout"
                        >
                            <LogOut className="h-4 w-4 mr-1" /> Abmelden
                        </Button>
                    </nav>

                    {/* Mobile hamburger (< md) — opens the slide-down panel below */}
                    <button
                        type="button"
                        className="md:hidden relative inline-flex items-center justify-center h-11 w-11 rounded-md text-white hover:bg-white/10 transition-colors"
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label={mobileOpen ? 'Menü schließen' : 'Menü öffnen'}
                        aria-expanded={mobileOpen}
                        aria-controls="member-mobile-menu"
                        data-testid="member-header-mobile-toggle"
                    >
                        {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        {!mobileOpen && totalBadges > 0 && (
                            <span
                                className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none shadow-sm ring-2 ring-secondary"
                                aria-label={`${totalBadges} neue Meldungen`}
                                data-testid="member-header-mobile-badge"
                            >
                                {totalBadges > 9 ? '9+' : totalBadges}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Mobile menu panel — slides down when hamburger is toggled. */}
            {mobileOpen && (
                <div
                    id="member-mobile-menu"
                    className="md:hidden border-t border-white/10 bg-secondary"
                    data-testid="member-mobile-menu"
                >
                    <nav className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-col gap-1" aria-label="Mobile Hauptnavigation">
                        {NAV_ITEMS.map((item) => {
                            const active = isActive(item);
                            const count = Number(badges[item.badgeKey] || 0);
                            return (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    className={`flex items-center justify-between px-4 py-3 min-h-[44px] rounded-md text-sm transition-colors ${
                                        active
                                            ? 'bg-white/15 text-white font-semibold'
                                            : 'text-gray-200 hover:text-white hover:bg-white/10'
                                    }`}
                                    aria-current={active ? 'page' : undefined}
                                    data-testid={`member-mobile-nav-${item.to.replace('/', '')}`}
                                >
                                    <span>{item.label}</span>
                                    {count > 0 && (
                                        <span
                                            className="min-w-[20px] h-[20px] px-1.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold leading-none"
                                            data-testid={`member-mobile-nav-badge-${item.to.replace('/', '')}`}
                                        >
                                            {count > 9 ? '9+' : count}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => { setMobileOpen(false); setReportOpen(true); }}
                            className="flex items-center gap-2 px-4 py-3 min-h-[44px] mt-2 rounded-md text-sm text-white bg-amber-600/90 hover:bg-amber-600 transition-colors font-medium"
                            data-testid="member-mobile-report-issue"
                        >
                            <AlertTriangle className="h-4 w-4" /> Fehler melden
                        </button>
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-4 py-3 min-h-[44px] rounded-md text-sm text-white bg-primary hover:bg-primary/80 transition-colors font-medium"
                            data-testid="member-mobile-logout"
                        >
                            <LogOut className="h-4 w-4" /> Abmelden
                        </button>
                    </nav>
                </div>
            )}
            <ReportIssueDialog open={reportOpen} onOpenChange={setReportOpen} />
        </header>
    );
}

export default MemberHeader;
